import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  User,
  Calendar,
  FileText,
  Download,
  QrCode,
  Mail,
  Phone,
  MapPin,
  Award,
  AlertCircle,
  Printer,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { Button } from '@components/common/Button';
import { Badge } from '@components/common/Badge';
import { Input } from '@components/forms/Input';
import { getLicenseByHashCode, verifyOTP, incrementVerificationCount } from '@mocks/verification';
import { LICENSE_CATEGORY_LABELS } from '@utils/constants';
import type { VerifiableLicense, Inspector } from '@mocks/verification';

export const InspectorVerify: React.FC = () => {
  const { hashcode } = useParams<{ hashcode: string }>();
  const navigate = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);

  const [inspector, setInspector] = useState<Inspector | null>(null);
  const [license, setLicense] = useState<VerifiableLicense | null>(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if inspector is logged in
    const pendingInspector = sessionStorage.getItem('pending_inspector');
    if (!pendingInspector) {
      toast.error('Please log in first');
      navigate('/inspector/login');
      return;
    }

    setInspector(JSON.parse(pendingInspector));

    // Load license if hashcode provided
    if (hashcode && hashcode !== 'pending') {
      const foundLicense = getLicenseByHashCode(hashcode);
      if (!foundLicense) {
        toast.error('License not found');
        navigate('/inspector/login');
        return;
      }
      setLicense(foundLicense);
    }
  }, [hashcode, navigate]);

  const handleVerifyOTP = async () => {
    if (!inspector || !otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 800));

    const isValid = verifyOTP(inspector.id, otp);

    if (!isValid) {
      toast.error('Invalid or expired OTP. Please try again or re-login.');
      setLoading(false);
      return;
    }

    if (license) {
      incrementVerificationCount(license.hash_code);
    }

    toast.success('OTP verified successfully!');
    setOtpVerified(true);
    setLoading(false);
  };

  const handlePrintPDF = () => {
    if (!printRef.current) return;
    
    toast.success('Preparing PDF for print...');
    window.print();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <Badge variant="success" icon={<CheckCircle className="h-3 w-3" />}>Active</Badge>;
      case 'EXPIRED':
        return <Badge variant="warning" icon={<AlertTriangle className="h-3 w-3" />}>Expired</Badge>;
      case 'SUSPENDED':
        return <Badge variant="warning" icon={<AlertTriangle className="h-3 w-3" />}>Suspended</Badge>;
      case 'REVOKED':
        return <Badge variant="danger" icon={<XCircle className="h-3 w-3" />}>Revoked</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  // OTP Input Screen
  if (!otpVerified) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-br from-ncaa-primary to-blue-700 text-white py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="h-8 w-8" />
              <h1 className="text-3xl font-bold">OTP Verification</h1>
            </div>
            <p className="text-blue-100">Enter the 6-digit code sent to your registered device</p>
          </div>
        </div>

        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Card>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Two-Factor Authentication</h2>
              <p className="text-gray-600">
                Inspector: {inspector?.full_name}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Badge: {inspector?.badge_number}
              </p>
            </div>

            {license && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-1">Verifying License</p>
                <p className="text-xs text-blue-700 font-mono">{license.license_number}</p>
                <p className="text-xs text-blue-700 mt-1">{license.holder_name}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter 6-Digit OTP
              </label>
              <Input
                type="text"
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtp(value);
                }}
                placeholder="000000"
                className="text-center text-2xl tracking-widest font-mono"
                maxLength={6}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Check the console for your OTP (demo mode)
              </p>
            </div>

            <Button
              onClick={handleVerifyOTP}
              fullWidth
              loading={loading}
              disabled={otp.length !== 6}
              icon={<CheckCircle className="h-4 w-4" />}
            >
              Verify & View License
            </Button>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                <div className="text-xs text-yellow-800">
                  <p className="font-medium mb-1">OTP was displayed after login</p>
                  <p>Check your console or the toast notification. OTP is valid for 10 minutes.</p>
                </div>
              </div>
            </div>

            <div className="mt-4 text-center">
              <button
                onClick={() => {
                  sessionStorage.removeItem('pending_inspector');
                  navigate('/inspector/login');
                }}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Back to Login
              </button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // License Details View (after OTP verified)
  if (!license) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <div className="text-center py-8">
            <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No License Selected</h3>
            <p className="text-gray-600 mb-4">Please scan a QR code to verify a license</p>
            <Button onClick={() => navigate('/inspector/login')}>
              Return to Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - No print */}
      <div className="bg-gradient-to-br from-ncaa-primary to-blue-700 text-white py-8 print:hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Shield className="h-8 w-8" />
                <h1 className="text-3xl font-bold">License Verification Complete</h1>
              </div>
              <p className="text-blue-100">Inspector: {inspector?.full_name} ({inspector?.badge_number})</p>
            </div>
            <Button
              onClick={handlePrintPDF}
              variant="outline"
              className="bg-white text-ncaa-primary hover:bg-gray-100"
              icon={<Download className="h-4 w-4" />}
            >
              Export PDF
            </Button>
          </div>
        </div>
      </div>

      <div ref={printRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ID Card View */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-ncaa-primary to-blue-600 text-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <QrCode className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Nigerian Civil Aviation Authority</h2>
                <p className="text-blue-100">Aviation Personnel License</p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Photo */}
              <div className="flex flex-col items-center">
                <div className="w-48 h-56 bg-gray-200 rounded-lg flex items-center justify-center mb-3">
                  <User className="h-24 w-24 text-gray-400" />
                </div>
                <div className="text-center">
                  {getStatusBadge(license.status)}
                </div>
              </div>

              {/* Personal Details */}
              <div className="md:col-span-2 space-y-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Full Name</p>
                  <p className="text-2xl font-bold text-gray-900">{license.holder_name}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">License Number</p>
                    <p className="font-mono font-semibold text-gray-900">{license.license_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">License Category</p>
                    <p className="font-semibold text-gray-900">
                      {LICENSE_CATEGORY_LABELS[license.license_category]}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Date of Birth</p>
                    <p className="font-medium text-gray-900">{formatDate(license.date_of_birth)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Nationality</p>
                    <p className="font-medium text-gray-900">{license.nationality}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Issue Date</p>
                    <p className="font-medium text-gray-900">{formatDate(license.issue_date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Expiry Date</p>
                    <p className="font-medium text-gray-900">{formatDate(license.expiry_date)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Ratings & Restrictions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {license.ratings && license.ratings.length > 0 && (
            <Card title="Ratings & Endorsements" icon={<Award className="h-5 w-5" />}>
              <ul className="space-y-2">
                {license.ratings.map((rating, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{rating}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {license.restrictions && license.restrictions.length > 0 && (
            <Card title="Restrictions & Limitations" icon={<AlertTriangle className="h-5 w-5" />}>
              <ul className="space-y-2">
                {license.restrictions.map((restriction, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{restriction}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        {/* Medical Information */}
        {license.medical_class && (
          <Card className="mb-6" title="Medical Certification" icon={<FileText className="h-5 w-5" />}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Medical Class</p>
                <p className="font-semibold text-gray-900">{license.medical_class}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Medical Expiry</p>
                <p className="font-semibold text-gray-900">
                  {license.medical_expiry ? formatDate(license.medical_expiry) : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Medical Status</p>
                {license.medical_expiry && new Date(license.medical_expiry) > new Date() ? (
                  <Badge variant="success">Valid</Badge>
                ) : (
                  <Badge variant="danger">Expired</Badge>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Verification History */}
        <Card title="Verification Information" icon={<Shield className="h-5 w-5" />}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">Issuing Authority</p>
              <p className="font-medium text-gray-900">{license.issuing_authority}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Verifications</p>
              <p className="font-semibold text-gray-900">{license.verification_count}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Last Verified</p>
              <p className="font-medium text-gray-900">
                {license.last_verified ? new Date(license.last_verified).toLocaleString() : 'N/A'}
              </p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-2">Current Verification Details</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Verified By:</p>
                <p className="font-medium text-gray-900">{inspector?.full_name}</p>
              </div>
              <div>
                <p className="text-gray-600">Badge Number:</p>
                <p className="font-medium text-gray-900">{inspector?.badge_number}</p>
              </div>
              <div>
                <p className="text-gray-600">Verification Date:</p>
                <p className="font-medium text-gray-900">{new Date().toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-600">Hash Code:</p>
                <p className="font-mono text-xs text-gray-900">{license.hash_code}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Action Buttons - No print */}
        <div className="flex gap-4 mt-6 print:hidden">
          <Button
            onClick={() => navigate('/inspector/login')}
            variant="outline"
            fullWidth
          >
            Verify Another License
          </Button>
          <Button
            onClick={handlePrintPDF}
            variant="primary"
            fullWidth
            icon={<Printer className="h-4 w-4" />}
          >
            Print/Save as PDF
          </Button>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #root, #root * {
            visibility: visible;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};
